import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  ArrowRight, 
  Phone, 
  Clock, 
  Users, 
  MessageSquare,
  Bot,
  Headphones,
  Calendar,
  Shield,
  Zap
} from 'lucide-react';
import IntakeModal from '../components/IntakeModal';

const PhoneAttendant = () => {
  const [isIntakeOpen, setIsIntakeOpen] = useState(false);
  const [intakeType, setIntakeType] = useState('client-zero');

  const openIntake = (type) => {
    setIntakeType(type);
    setIsIntakeOpen(true);
  };

  // Problems businesses face
  const problems = [
    "Customers waiting for answers",
    "Leads waiting for follow-up",
    "Repetitive questions consuming staff time",
    "After-hours callers with no response",
    "Missed calls during busy periods",
    "Staff overwhelmed with administrative tasks",
    "Lost opportunities from slow response times"
  ];

  // What AVA Receptionist can do
  const avaCapabilities = [
    { title: "RESPOND", description: "Handle approved customer and prospect inquiries instantly" },
    { title: "QUALIFY", description: "Collect information and identify opportunities requiring human attention" },
    { title: "SCHEDULE", description: "Help customers and prospects move to the next step" },
    { title: "ROUTE", description: "Direct inquiries to the appropriate person or department" },
    { title: "FOLLOW UP", description: "Initiate approved follow-up workflows based on business rules" },
    { title: "ESCALATE", description: "Bring employees into the conversation when human judgment is required" }
  ];

  // Industries
  const industries = [
    "Property Management",
    "Professional Services",
    "Healthcare Practices",
    "Real Estate Teams",
    "Home Services",
    "Legal Firms",
    "Financial Services",
    "Nonprofits",
    "Service Businesses"
  ];

  // AVA vs Traditional comparison
  const comparison = [
    { traditional: "Missed calls = lost opportunities", ava: "24/7 availability — never miss an inquiry" },
    { traditional: "Staff answering repetitive questions", ava: "AI handles routine inquiries automatically" },
    { traditional: "Slow response times", ava: "Instant responses to customers and leads" },
    { traditional: "High cost per interaction", ava: "Reduced cost through intelligent automation" },
    { traditional: "Inconsistent customer experience", ava: "Consistent, professional service every time" },
    { traditional: "Limited to business hours", ava: "Works when you can't — evenings, weekends, holidays" }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F5F5F5] via-white to-[#F5F5F5]"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#227AFF]/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-[#227AFF]/10 px-4 py-2 rounded-full mb-6">
              <Bot className="h-5 w-5 text-[#227AFF]" />
              <span className="text-[#227AFF] font-semibold">Powered by AVA™</span>
            </div>
            
            <h1 className="mb-6">
              Your <span className="text-gradient">AI Receptionist</span>
            </h1>
            <p className="text-2xl font-semibold text-gray-700 mb-8">
              Handle customer inquiries, qualify leads, and schedule appointments — even when your team is focused elsewhere.
            </p>
            
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-10">
              AVA Receptionist is an AI employee that participates in your customer communication workflows while keeping your people in control of the decisions that matter.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/book-consultation" className="btn btn-primary text-lg px-8">
                Book a Strategy Session
              </Link>
              <button onClick={() => openIntake('meet-ava')} className="btn btn-glass text-lg px-8 inline-flex items-center gap-2">
                Learn About AVA <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">Your Business Is Already Telling You Where AI Belongs</h2>
            <div className="accent-line"></div>
            
            <p className="text-lg text-gray-600 text-center mt-6 mb-10">
              Look at where your team spends its time handling customer communication:
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
              <div className="grid sm:grid-cols-2 gap-4">
                {problems.map((problem, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">{problem}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#227AFF]/5 to-[#007BFF]/5 rounded-xl p-6 text-center">
              <p className="text-gray-700 mb-2">These aren't simply operational annoyances.</p>
              <p className="text-xl font-semibold text-[#0A1538]">They're opportunities for an AI employee.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet AVA Receptionist */}
      <section className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-gray-400 mb-2">Meet Your AI Receptionist</p>
              <h2 className="text-white mb-2">AVA™ Receptionist</h2>
              <div className="accent-line mt-4"></div>
            </div>

            <p className="text-lg text-gray-300 text-center mb-10 max-w-3xl mx-auto">
              AVA Receptionist is configured to handle approved customer inquiries, collect information, route requests, schedule appointments and escalate conversations when human assistance is required.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {avaCapabilities.map((cap, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10">
                  <h4 className="text-[#227AFF] font-bold mb-2">{cap.title}</h4>
                  <p className="text-gray-300 text-sm">{cap.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center border border-white/10">
              <p className="text-gray-300 mb-2">AVA isn't designed to replace your team.</p>
              <p className="text-white font-semibold">AVA handles the repetitive work so your people can focus on what matters.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-center mb-4">Traditional Reception vs. AVA Receptionist</h2>
            <div className="accent-line"></div>

            <div className="mt-12 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left p-4 bg-red-50 rounded-tl-xl">
                      <span className="text-red-600 font-semibold">Traditional Approach</span>
                    </th>
                    <th className="text-left p-4 bg-green-50 rounded-tr-xl">
                      <span className="text-green-600 font-semibold">With AVA Receptionist</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-4 border-b border-gray-100">
                        <div className="flex items-start gap-2">
                          <span className="text-red-500">×</span>
                          <span className="text-gray-700">{row.traditional}</span>
                        </div>
                      </td>
                      <td className="p-4 border-b border-gray-100">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700">{row.ava}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Humans Stay in Control */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">Humans Stay in Control</h2>
            <div className="accent-line"></div>
            <p className="text-xl text-[#227AFF] font-semibold text-center mt-6 mb-8">
              AI should amplify human potential — not remove human accountability.
            </p>

            <p className="text-lg text-gray-600 text-center mb-8">
              AVA Receptionist is designed around human oversight. Your organization determines:
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {[
                'What AVA can handle independently',
                'What requires human approval',
                'When AVA must escalate',
                'What information AVA can access',
                'Which systems AVA can interact with'
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-lg p-4 flex items-center gap-3 shadow-sm">
                  <Shield className="h-5 w-5 text-[#227AFF] flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="bg-[#0A1538] rounded-xl p-6 text-center">
              <p className="text-gray-300 mb-2">AVA handles the repetitive communication work.</p>
              <p className="text-xl font-semibold text-white">Your people handle the decisions that need people.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">Who Benefits from AVA Receptionist</h2>
            <div className="accent-line"></div>
            <p className="text-lg text-gray-600 text-center mt-6 mb-10">
              Any organization that handles customer inquiries can benefit from an AI receptionist:
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-10">
              {industries.map((industry, index) => (
                <div key={index} className="flex items-center gap-2 bg-white rounded-lg p-4 shadow-sm">
                  <CheckCircle className="h-5 w-5 text-[#227AFF] flex-shrink-0" />
                  <span className="text-gray-700">{industry}</span>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-[#227AFF]/5 to-[#007BFF]/5 rounded-xl p-6 text-center">
              <p className="text-lg text-gray-700">
                If your business handles customer communication, AVA Receptionist can help.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Zero CTA */}
      <section className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-gray-400 mb-2">Ready to Deploy Your AI Receptionist?</p>
            <h2 className="text-white mb-4">Join the AVA Client Zero Program</h2>
            
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              We're selecting a limited number of businesses to implement AVA Receptionist through our founding customer program.
            </p>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10 mb-8">
              <p className="text-white font-semibold mb-4">Client Zero includes:</p>
              <div className="grid sm:grid-cols-2 gap-3 text-left max-w-xl mx-auto">
                {[
                  '1 AVA AI Employee configuration',
                  'Workflow discovery and design',
                  'Business rules and escalation setup',
                  'Integration planning',
                  'Testing and deployment',
                  '30-day optimization period'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[#227AFF] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => openIntake('client-zero')} className="btn btn-accent" data-testid="apply-client-zero-btn">
                Apply for Client Zero
              </button>
              <Link to="/book-consultation" className="btn btn-glass inline-flex items-center gap-2">
                Book a Strategy Session <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-4">Start With One Customer Communication Problem</h2>
            <p className="text-xl text-gray-600 mb-8">
              You don't need to automate everything. Start with the workflow that's consuming the most time or losing the most opportunities.
            </p>
            
            <div className="bg-gradient-to-r from-[#227AFF]/5 to-[#007BFF]/5 rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 mb-2">Start With the Problem.</p>
              <p className="text-2xl font-bold text-[#0A1538]">We'll Help You Build the Solution.</p>
            </div>

            <Link to="/book-consultation" className="btn btn-primary text-lg px-8">
              Book Your AI Strategy Session
            </Link>
            
            <p className="text-sm text-gray-500 mt-6">
              We'll help you determine whether AVA Receptionist is right for your business.
            </p>
          </div>
        </div>
      </section>

      {/* Intake Modal */}
      <IntakeModal 
        isOpen={isIntakeOpen} 
        onClose={() => setIsIntakeOpen(false)} 
        type={intakeType}
      />
    </div>
  );
};

export default PhoneAttendant;
